import { elementEvaluation } from "@/lib/element-evaluations";
import { ELEMENT_CODES } from "@/lib/elements";
import { POWER_GROUPS, POWER_LIBRARY, type PowerTag } from "@/lib/power-library";
import type { LevelMap, ScoreSource } from "@/lib/score-source";

// The renderer iframe has an IntlProvider but no Apollo, so the payload carries finished data
// (@adr SPARK-0044-match-result-pdfs-use-a-dedicated-spa-renderer@3).

// Fixed column order. The user picks WHICH sources print, never their order, so two exports with
// the same ticks always read the same way.
export const POWER_PROFILE_PDF_SOURCES: readonly ScoreSource[] = ["self", "smart", "combined"];

// Which ticked source the evaluation text is taken from: Combined is the best single estimate,
// then the user's own rating, then the document reading.
const EVALUATION_PRIORITY: readonly ScoreSource[] = ["combined", "self", "smart"];

export type PowerProfilePdfSourceSettings = Record<ScoreSource, boolean>;

export interface PowerProfilePdfSettings {
  sources: PowerProfilePdfSourceSettings;
  details: boolean;
}

// Printed glyphs outside the message bundles; both must stay inside WinAnsi.
export const POWER_PROFILE_PDF_ABSENT = "\u2013";
export const POWER_PROFILE_PDF_SEPARATOR = "  \u00B7  ";

export const DEFAULT_POWER_PROFILE_PDF_SETTINGS: PowerProfilePdfSettings = {
  sources: { self: true, smart: true, combined: false },
  details: true,
};

export interface PowerProfilePdfPower {
  abbr: string;
  nameId: string;
  // One entry per payload `sources` entry, same order. null = not measured in that source
  // (@adr SPARK-0037-unmeasured-powers-are-absent-not-zero@2): printed as absent, never as 0.
  scores: (number | null)[];
  // The source the evaluation is read from, null when no ticked source has a value. text is null
  // for a measured 0, which sits below the lowest band; the source is still named.
  evaluation: { source: ScoreSource; text: string | null } | null;
}

export interface PowerProfilePdfCluster {
  tag: PowerTag;
  powers: PowerProfilePdfPower[];
}

export interface PowerProfilePdfCoverage {
  source: ScoreSource;
  measured: number;
  total: number;
}

export interface PowerProfilePdfPayload {
  fileName: string;
  locale: string;
  personName: string;
  exportedAt: string;
  sources: ScoreSource[];
  coverage: PowerProfilePdfCoverage[];
  clusters: PowerProfilePdfCluster[];
  details: boolean;
}

const ELEMENT_BY_ABBR: Record<string, string> = Object.fromEntries(
  Object.entries(ELEMENT_CODES).map(([element, abbr]) => [abbr, element]),
);

export function selectedSources(settings: PowerProfilePdfSourceSettings): ScoreSource[] {
  return POWER_PROFILE_PDF_SOURCES.filter((source) => settings[source]);
}

function valueOf(levels: LevelMap, abbr: string): number | null {
  const value = levels.get(abbr);
  return typeof value === "number" ? value : null;
}

// Local calendar date, not toISOString: an export at 00:30 in Berlin must not carry yesterday.
function localDate(date: Date): string {
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
}

export function powerProfilePdfFileName(prefix: string, personName: string, date: Date): string {
  const name = personName
    .trim()
    .replace(/[\\/:*?"<>|]+/g, "")
    .replace(/\s+/g, "-");
  return `${prefix}_${name ? `${name}_` : ""}${localDate(date)}.pdf`;
}

export function buildPowerProfilePdfPayload({
  levels,
  settings,
  fileNamePrefix,
  personName,
  locale,
  now,
}: {
  levels: Record<ScoreSource, LevelMap>;
  settings: PowerProfilePdfSettings;
  fileNamePrefix: string;
  personName: string;
  locale: string;
  now: Date;
}): PowerProfilePdfPayload {
  const sources = selectedSources(settings.sources);
  const leading = EVALUATION_PRIORITY.filter((source) => settings.sources[source]);

  const clusters = POWER_GROUPS.map((tag) => ({
    tag,
    powers: POWER_LIBRARY.filter((power) => power.tag === tag).map((power) => {
      const lead = leading.find((source) => valueOf(levels[source], power.abbr) != null);
      const text =
        lead != null
          ? elementEvaluation(ELEMENT_BY_ABBR[power.abbr] ?? "", valueOf(levels[lead], power.abbr), locale)
          : null;
      return {
        abbr: power.abbr,
        nameId: power.nameId,
        scores: sources.map((source) => valueOf(levels[source], power.abbr)),
        evaluation: lead != null ? { source: lead, text } : null,
      };
    }),
  }));

  return {
    fileName: powerProfilePdfFileName(fileNamePrefix, personName, now),
    locale,
    personName,
    exportedAt: now.toISOString(),
    sources,
    coverage: sources.map((source) => ({
      source,
      measured: POWER_LIBRARY.filter((power) => valueOf(levels[source], power.abbr) != null).length,
      total: POWER_LIBRARY.length,
    })),
    clusters,
    details: settings.details,
  };
}
